import { useState, useCallback, useRef, useEffect } from 'react';

type ValidationRule = (value: any) => string;

interface FieldRegistration {
    value: any;
    rules: ValidationRule[];
    label?: string;
}

export const useFormValidation = () => {
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    // Track registered fields - use ref to avoid re-renders
    const registeredFields = useRef<Record<string, FieldRegistration>>({});

    const errorsRef = useRef<Record<string, string>>({});

    const touchedRef = useRef(touched);
    const attemptedSubmitRef = useRef(attemptedSubmit);

    useEffect(() => {
        touchedRef.current = touched;
    }, [touched]);
    useEffect(() => {
        attemptedSubmitRef.current = attemptedSubmit;
    }, [attemptedSubmit]);

    useEffect(() => {
        errorsRef.current = errors;
    }, [errors]);

    /**
     * Validate a single field with its rules
     */
    const validateField = useCallback((name: string, value: any, rules: ValidationRule[]): string => {
        for (const rule of rules) {
            const error = rule(value);
            if (error) return error;
        }
        return '';
    }, []);

    /**
     * Register a field with its initial value and rules
     */
    const registerField = useCallback((name: string, value: any, rules: ValidationRule[] = [], label?: string) => {
        registeredFields.current[name] = {
            value,
            rules,
            label: label ?? registeredFields.current[name]?.label
        };
    }, []);

    /**
     * Unregister a field and clean up its state
     */
    const unregisterField = useCallback((name: string) => {
        delete registeredFields.current[name];

        // Use functional updates to ensure we're working with latest state
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const { [name]: _, ...rest } = prev;
            errorsRef.current = rest;
            return rest;
        });

        setTouched((prev) => {
            if (!prev[name]) return prev;
            const { [name]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    /**
     * Update field value (no state change, just ref update)
     */
    const updateFieldValue = useCallback((name: string, value: any) => {
        const field = registeredFields.current[name];
        if (field) {
            field.value = value;
        }
    }, []);

    /**
     * Update field validity state
     */
    const updateFieldValidity = useCallback((fieldName: string, isValid: boolean, error: string = '') => {
        setErrors((prev) => {
            if (prev[fieldName] === error) return prev;
            const next = { ...prev, [fieldName]: error };
            errorsRef.current = next;
            return next;
        });

        if (!isValid) {
            setTouched((prev) => {
                if (prev[fieldName]) return prev; // Already touched
                return { ...prev, [fieldName]: true };
            });
        }
    }, []);

    /**
     * Handle field change - validate if touched or submitted
     */
    const handleChange = useCallback(
        (name: string, value: any, rules: ValidationRule[] = []) => {
            updateFieldValue(name, value);

            // Validate and update error if field is touched or form was submitted
            setErrors((prevErrors) => {
                const shouldValidate = touchedRef.current[name] || attemptedSubmitRef.current;

                if (!shouldValidate) return prevErrors;

                const error = validateField(name, value, rules);
                if (prevErrors[name] === error) return prevErrors;

                const next = { ...prevErrors, [name]: error };
                errorsRef.current = next;
                return next;
            });
        },
        [updateFieldValue, validateField]
    );

    /**
     * Handle field blur - mark touched and validate IMMEDIATELY
     */
    const handleBlur = useCallback(
        (name: string, value: any, rules: ValidationRule[] = []) => {
            const error = validateField(name, value, rules);

            setTouched((prev) => ({ ...prev, [name]: true }));
            setErrors((prev) => {
                const next = { ...prev, [name]: error };
                errorsRef.current = next;
                return next;
            });
        },
        [validateField]
    );

    /**
     * Validate all registered fields
     */
    const validateAll = useCallback((): boolean => {
        const newErrors: Record<string, string> = {};
        const newTouched: Record<string, boolean> = {};

        Object.entries(registeredFields.current).forEach(([fieldName, registration]) => {
            const error = validateField(fieldName, registration.value, registration.rules);
            if (error) {
                newErrors[fieldName] = error;
            }
            newTouched[fieldName] = true;
        });

        errorsRef.current = newErrors;

        setErrors(newErrors);
        setTouched(newTouched);
        setAttemptedSubmit(true);

        return Object.keys(newErrors).length === 0;
    }, [validateField]);

    /**
     * Validate specific field by name
     */
    const validateFieldByName = useCallback(
        (name: string): string => {
            const registration = registeredFields.current[name];
            if (!registration) return '';

            const error = validateField(name, registration.value, registration.rules);

            setErrors((prev) => {
                if (prev[name] === error) return prev;
                const next = { ...prev, [name]: error };
                errorsRef.current = next;
                return next;
            });

            return error;
        },
        [validateField]
    );

    /**
     * Get error for a specific field
     */
    const getFieldError = useCallback(
        (fieldName: string): string => {
            // Return error only if touched
            return touched[fieldName] ? errors[fieldName] || '' : '';
        },
        [touched, errors]
    );

    /**
     * Check if a field has an error
     */
    const hasFieldError = useCallback(
        (fieldName: string): boolean => {
            return touched[fieldName] && !!errors[fieldName];
        },
        [touched, errors]
    );

    /**
     * Check if a field is touched
     */
    const isFieldTouched = useCallback(
        (fieldName: string): boolean => {
            return touched[fieldName];
        },
        [touched]
    );

    /**
     * Set touched state for a specific field
     */
    const setFieldTouched = useCallback((fieldName: string, isTouched: boolean) => {
        setTouched((prev) => {
            if (prev[fieldName] === isTouched) return prev;
            return { ...prev, [fieldName]: isTouched };
        });
    }, []);

    /**
     * Reset all validation state
     */
    const reset = useCallback(() => {
        errorsRef.current = {};
        setTouched({});
        setErrors({});
        setAttemptedSubmit(false);
    }, []);

    /**
     * Check if form is valid
     */
    const isValid = useCallback((): boolean => {
        return Object.values(errors).every((error) => !error);
    }, [errors]);

    /**
     * Get list of registered field names
     */
    const getRegisteredFields = useCallback((): string[] => {
        return Object.keys(registeredFields.current);
    }, []);

    const getFieldLabels = useCallback((): Record<string, string> => {
        const labels: Record<string, string> = {};
        for (const [name, field] of Object.entries(registeredFields.current)) {
            labels[name] = field.label ?? name;
        }
        return labels;
    }, []);

    const getCurrentErrors = useCallback((): Record<string, string> => {
        return errorsRef.current;
    }, []);

    return {
        // State (triggers re-render when changed)
        touched,
        errors,
        attemptedSubmit,

        // Registration (stable)
        registerField,
        unregisterField,
        updateFieldValue,
        updateFieldValidity,

        // Validation (stable)
        validateAll,
        validateField: validateFieldByName,
        handleBlur,
        handleChange,

        // Query (immediate)
        getFieldError,
        hasFieldError,
        isFieldTouched,
        isValid,
        getRegisteredFields,
        getCurrentErrors,
        getFieldLabels,

        // Actions (stable)
        reset,
        setTouched,
        setErrors
    };
};

export default useFormValidation;
